import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash, ClockCountdown, Users, Stack, ShieldStar, Notebook,
  Kanban, FileArrowUp, MapTrifold, WarningOctagon, CheckCircle, Circle,
  ArrowsOut, DownloadSimple, Gavel, FloppyDisk,
} from "@phosphor-icons/react";

const POSTURE = [
  ["ready", "Ready"], ["gap_identified", "Gap identified"], ["in_progress", "In progress"],
  ["potential_noncompliance", "Potential noncompliance"],
];
const CERT_RESPONSE = [
  ["compliant", "Compliant"], ["non_compliant", "Not compliant"],
  ["no_facilities", "No Facilities"], ["not_applicable", "Not applicable"],
];
const CERT_STYLE = {
  compliant: "bg-emerald-100 text-emerald-800 border-emerald-300",
  non_compliant: "bg-red-100 text-red-700 border-red-300",
  no_facilities: "bg-slate-100 text-slate-600 border-slate-300",
  not_applicable: "bg-slate-100 text-slate-600 border-slate-300",
};
const POSTURE_STYLE = {
  ready: "bg-emerald-100 text-emerald-800 border-emerald-300",
  gap_identified: "bg-amber-100 text-amber-800 border-amber-300",
  in_progress: "bg-blue-100 text-blue-700 border-blue-300",
  potential_noncompliance: "bg-red-100 text-red-700 border-red-300",
};
const STATUSES = [
  "draft", "scoping", "evidence_staging", "active_review", "fieldwork_walkdown",
  "draft_report", "comment_period", "finalized", "closed", "escalated_to_preliminary_screen",
];
const CLOCK_STYLE = {
  overdue: "border-red-300 bg-red-50 text-red-700",
  due_soon: "border-amber-300 bg-amber-50 text-amber-800",
  upcoming: "border-border bg-card text-foreground",
  pending: "border-slate-200 bg-slate-50 text-slate-500",
  complete: "border-emerald-300 bg-emerald-50 text-emerald-700",
};

function Section({ title, icon: Icon, children, action }) {
  return (
    <div className="border border-border bg-card">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-secondary/40">
        <div className="flex items-center gap-2 text-sm font-semibold">{Icon && <Icon size={16} />}{title}</div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function ClockRow({ eid, c, onChange }) {
  const toggle = async () => {
    try { const r = await api.post(`/engagements/${eid}/milestone/${c.key}/toggle`); onChange(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const days = c.days_remaining;
  const human = c.status === "complete" ? "Complete"
    : c.status === "pending" ? "Needs a date"
      : c.status === "overdue" ? `${Math.abs(days)}d overdue`
        : `${days}d left`;
  return (
    <div data-testid={`clock-${c.key}`} className={`border px-3 py-2 ${CLOCK_STYLE[c.status]} flex items-start gap-2`}>
      {c.type === "deadline" ? (
        <button data-testid={`clock-toggle-${c.key}`} onClick={toggle} className="mt-0.5 shrink-0">
          {c.done ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} />}
        </button>
      ) : <ClockCountdown size={16} className="mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold flex items-center justify-between gap-2">
          <span className="truncate">{c.label}</span>
          <span className="font-mono shrink-0">{human}</span>
        </div>
        <div className="text-[10px] opacity-80">{c.due_date || "—"} · {c.citation}{c.type === "check" ? " · adequacy check" : ""}</div>
        {c.note && <div className="text-[10px] opacity-70 mt-0.5">{c.note}</div>}
      </div>
    </div>
  );
}

export default function EngagementDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [eng, setEng] = useState(null);
  const [standards, setStandards] = useState([]);
  const [scopeDlg, setScopeDlg] = useState(false);
  const [expandDlg, setExpandDlg] = useState(false);
  const [attestDlg, setAttestDlg] = useState(false);
  const [attestForm, setAttestForm] = useState({ officer_name: "", officer_title: "", statement: "" });
  const [scopeForm, setScopeForm] = useState({ standard_code: "", requirement_number: "", applicability_rationale: "" });
  const [expandForm, setExpandForm] = useState({ standard_code: "", requirement_number: "", reason: "" });

  const load = useCallback(() => {
    api.get(`/engagements/${id}`).then((r) => setEng(r.data)).catch(() => toast.error("Could not load engagement"));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/standards").then((r) => setStandards(r.data || [])).catch(() => {}); }, []);

  if (!eng) return <div className="p-10 font-mono text-sm">Loading engagement…</div>;

  const setStatus = async (status) => {
    try { const r = await api.put(`/engagements/${id}`, { status }); setEng(r.data); toast.success("Status updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addScope = async () => {
    if (!scopeForm.standard_code) { toast.error("Pick a standard"); return; }
    try {
      const r = await api.post(`/engagements/${id}/scope`, { items: [scopeForm] });
      setEng(r.data); setScopeDlg(false);
      setScopeForm({ standard_code: "", requirement_number: "", applicability_rationale: "" });
      toast.success("Scope item added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const expandScope = async () => {
    if (!expandForm.standard_code) { toast.error("Pick a standard"); return; }
    try {
      const r = await api.post(`/engagements/${id}/expand-scope`, {
        items: [{ standard_code: expandForm.standard_code, requirement_number: expandForm.requirement_number, applicability_rationale: "Added via scope expansion" }],
        reason: expandForm.reason,
      });
      setEng(r.data); setExpandDlg(false); setExpandForm({ standard_code: "", requirement_number: "", reason: "" });
      toast.success("Scope expanded — history archived");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setPosture = async (sid, posture) => {
    try { await api.put(`/engagements/${id}/scope/${sid}`, { compliance_posture: posture }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setCertResponse = async (sid, certification_response) => {
    try { await api.put(`/engagements/${id}/scope/${sid}`, { certification_response }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const attest = async () => {
    if (!attestForm.officer_name.trim()) { toast.error("Officer name is required"); return; }
    try { const r = await api.post(`/engagements/${id}/attest`, attestForm); setEng(r.data); setAttestDlg(false); toast.success("Self-Certification signed & submitted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const revokeAttest = async () => {
    try { await api.delete(`/engagements/${id}/attest`); load(); toast.success("Signature revoked — reopened for edits"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delScope = async (sid) => { await api.delete(`/engagements/${id}/scope/${sid}`); load(); };
  const toPrelim = async (sid) => {
    try { const r = await api.post(`/engagements/${id}/scope/${sid}/to-preliminary-screen`, {}); toast.success("Transferred to Preliminary Screen (PNC created)"); load(); setTimeout(() => { if (window.confirm("Open the PNC Tracker?")) nav(r.data.pnc_url); }, 200); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ap = eng.audit_period || {};
  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="engagement-detail">
      <button onClick={() => nav("/engagements")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft size={15} /> All engagements
      </button>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="overline text-muted-foreground mb-1">{(eng.engagement_type || "").replace(/_/g, " ")}{eng.engagement_type === "compliance_audit" ? ` · ${eng.audit_sub_type}` : ""} · {eng.regional_entity || "No CEA set"}</div>
          <h1 className="text-2xl font-bold">{eng.title}</h1>
          <div className="text-xs text-muted-foreground mt-1">Lead CEA Auditor: {eng.lead_cea_auditor || "—"}</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={eng.status} onValueChange={setStatus}>
            <SelectTrigger data-testid="eng-status-select" className="rounded-none w-56"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {eng.engagement_type === "compliance_audit" && (
        <div data-testid="audit-period" className="border border-border bg-secondary/30 px-4 py-3 mb-4 text-sm flex flex-wrap gap-x-8 gap-y-1">
          <span><b>Audit period (Sec 4.1.3.2):</b> {ap.start || "—"} → {ap.end || "—"}</span>
          <span className="text-muted-foreground">Suggested end (notice + 30d): {ap.suggested_end || "—"}</span>
          {eng.fac008_scoped && <Badge className="rounded-none bg-teal-100 text-teal-800 border-teal-300" variant="outline">FAC-008 walkdown active</Badge>}
        </div>
      )}

      {eng.engagement_type === "spot_check" && (
        <div data-testid="nonpublic-banner" className="border border-amber-400 bg-amber-50 text-amber-900 px-4 py-2 mb-4 text-sm font-semibold flex items-center gap-2">
          <WarningOctagon size={16} weight="fill" /> NON-PUBLIC / NOT FOR PUBLIC DISCLOSURE — Spot Check records are confidential (App 4C Sec 4.3.1).
        </div>
      )}

      {eng.engagement_type === "self_certification" && (
        <div data-testid="attestation-banner" className="border border-border bg-secondary/30 px-4 py-3 mb-4 text-sm flex items-center justify-between gap-4 flex-wrap">
          {eng.attestation ? (
            <>
              <span className="flex items-center gap-2 text-emerald-800">
                <CheckCircle size={16} weight="fill" /> Signed & submitted by <b>{eng.attestation.officer_name}</b>{eng.attestation.officer_title ? `, ${eng.attestation.officer_title}` : ""} · {(eng.attestation.attested_at || "").slice(0, 10)}
              </span>
              <Button data-testid="revoke-attest-btn" size="sm" variant="outline" className="rounded-none h-7" onClick={revokeAttest}>Revoke signature</Button>
            </>
          ) : (
            <>
              <span className="text-muted-foreground">Authorized-representative electronic signature required before submittal (App 4C Sec 4.2). Set a certification response on every scoped requirement first.</span>
              <Button data-testid="attest-btn" size="sm" className="rounded-none h-7" onClick={() => setAttestDlg(true)}><Gavel size={13} className="mr-1" /> Sign &amp; Submit</Button>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT — scope tree */}
        <div className="lg:col-span-3 space-y-4">
          <Section title="Scoped Standards" icon={Stack}
            action={<Button data-testid="add-scope-btn" size="sm" variant="outline" className="rounded-none h-7" onClick={() => setScopeDlg(true)}><Plus size={13} /></Button>}>
            <div className="space-y-2">
              {(eng.scope_items || []).length === 0 && <div className="text-xs text-muted-foreground">No requirements scoped yet.</div>}
              {(eng.scope_items || []).map((s) => (
                <div key={s.id} data-testid={`scope-${s.id}`} className="border border-border p-2">
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-mono font-bold text-xs">{s.standard_code} {s.requirement_number}</span>
                    {s.expanded && <Badge variant="outline" className="rounded-none text-[9px] bg-purple-100 text-purple-700 border-purple-300">Expanded</Badge>}
                  </div>
                  {eng.engagement_type === "self_certification" ? (
                    <Select value={s.certification_response || ""} onValueChange={(v) => setCertResponse(s.id, v)}>
                      <SelectTrigger data-testid={`scope-cert-${s.id}`} className={`rounded-none h-6 text-[10px] mt-1 ${CERT_STYLE[s.certification_response] || ""}`}><SelectValue placeholder="Set response…" /></SelectTrigger>
                      <SelectContent>{CERT_RESPONSE.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Select value={s.compliance_posture} onValueChange={(v) => setPosture(s.id, v)}>
                      <SelectTrigger data-testid={`scope-posture-${s.id}`} className={`rounded-none h-6 text-[10px] mt-1 ${POSTURE_STYLE[s.compliance_posture] || ""}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{POSTURE.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <div className="flex items-center gap-1 mt-1.5">
                    {s.rsaw_url && <Link to={s.rsaw_url} data-testid={`scope-rsaw-${s.id}`} className="text-[10px] underline text-blue-700 flex items-center gap-0.5"><Notebook size={11} /> RSAW</Link>}
                    <button data-testid={`scope-prelim-${s.id}`} onClick={() => toPrelim(s.id)} title="Transfer to Preliminary Screen" className="text-[10px] underline text-red-600 flex items-center gap-0.5 ml-1"><Gavel size={11} /> Prelim</button>
                    <button data-testid={`scope-del-${s.id}`} onClick={() => delScope(s.id)} className="ml-auto text-muted-foreground hover:text-red-600"><Trash size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
            <Button data-testid="expand-scope-btn" variant="outline" className="rounded-none w-full mt-3 h-8 text-xs" onClick={() => setExpandDlg(true)}>
              <ArrowsOut size={13} className="mr-1" /> Expand Scope / Add Requirements
            </Button>
          </Section>
        </div>

        {/* CENTER — work tabs */}
        <div className="lg:col-span-6">
          <Tabs defaultValue="evidence">
            <TabsList className="rounded-none flex-wrap h-auto">
              <TabsTrigger value="evidence" data-testid="tab-evidence" className="rounded-none"><ShieldStar size={14} className="mr-1" /> Evidence (ERT)</TabsTrigger>
              <TabsTrigger value="tasks" data-testid="tab-tasks" className="rounded-none"><Kanban size={14} className="mr-1" /> Tasks</TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-none"><FileArrowUp size={14} className="mr-1" /> Reports</TabsTrigger>
              {eng.fac008_scoped && <TabsTrigger value="walkdown" data-testid="tab-walkdown" className="rounded-none"><MapTrifold size={14} className="mr-1" /> Walkdown</TabsTrigger>}
            </TabsList>
            <TabsContent value="evidence" className="mt-3"><ErtStaging eid={id} /></TabsContent>
            <TabsContent value="tasks" className="mt-3"><TasksPane eid={id} eng={eng} /></TabsContent>
            <TabsContent value="reports" className="mt-3"><ReportsPane eid={id} eng={eng} reload={load} /></TabsContent>
            {eng.fac008_scoped && <TabsContent value="walkdown" className="mt-3"><WalkdownPane eid={id} eng={eng} reload={load} /></TabsContent>}
          </Tabs>
        </div>

        {/* RIGHT — clocks, team, expansion */}
        <div className="lg:col-span-3 space-y-4">
          <Section title="Regulatory Clocks" icon={ClockCountdown}>
            <div className="space-y-2">
              {(eng.clocks || []).length === 0 && <div className="text-xs text-muted-foreground">Add dates to the engagement to generate clocks.</div>}
              {(eng.clocks || []).map((c) => <ClockRow key={c.key} eid={id} c={c} onChange={setEng} />)}
            </div>
          </Section>
          <TeamPane eid={id} eng={eng} reload={load} />
          {(eng.expansion_history || []).length > 0 && (
            <Section title="Scope Expansion Log" icon={ArrowsOut}>
              <div className="space-y-2">
                {eng.expansion_history.map((h, i) => (
                  <div key={i} className="border border-border p-2 text-[11px]">
                    <div className="font-semibold">{(h.expanded_date || "").slice(0, 10)} — {h.authorized_by}</div>
                    <div className="text-muted-foreground">+ {h.new_scope.join(", ")}</div>
                    {h.reason && <div className="italic mt-0.5">“{h.reason}”</div>}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>

      {/* Add scope dialog */}
      <Dialog open={scopeDlg} onOpenChange={setScopeDlg}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Add Scoped Requirement</DialogTitle></DialogHeader>
          <ScopePicker standards={standards} form={scopeForm} setForm={setScopeForm} />
          <DialogFooter><Button data-testid="scope-submit" onClick={addScope} className="rounded-none">Add to scope</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Expand scope dialog */}
      <Dialog open={expandDlg} onOpenChange={setExpandDlg}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Expand Scope</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2 mb-1">Archives the current scope into the expansion log, appends the new requirement, and preserves unchanged milestones.</p>
          <ScopePicker standards={standards} form={expandForm} setForm={setExpandForm} />
          <div><Label>Reason for expansion</Label>
            <Textarea data-testid="expand-reason" value={expandForm.reason} onChange={(e) => setExpandForm({ ...expandForm, reason: e.target.value })} className="rounded-none mt-1" placeholder="e.g. CEA issued mid-audit deep-dive on CIP-010 R1" /></div>
          <DialogFooter><Button data-testid="expand-submit" onClick={expandScope} className="rounded-none">Expand scope</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Self-Certification attestation dialog */}
      <Dialog open={attestDlg} onOpenChange={setAttestDlg}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Sign &amp; Submit Self-Certification</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Electronic signature of the authorized representative (App 4C Sec 4.2). Your account identity and timestamp are recorded alongside the officer details.</p>
          <div className="space-y-3">
            <div><Label>Attesting officer name</Label>
              <Input data-testid="attest-name" value={attestForm.officer_name} onChange={(e) => setAttestForm({ ...attestForm, officer_name: e.target.value })} className="rounded-none mt-1" placeholder="e.g. Jane Compliance" /></div>
            <div><Label>Officer title</Label>
              <Input data-testid="attest-title" value={attestForm.officer_title} onChange={(e) => setAttestForm({ ...attestForm, officer_title: e.target.value })} className="rounded-none mt-1" placeholder="e.g. VP Compliance" /></div>
            <div><Label>Attestation statement (optional override)</Label>
              <Textarea data-testid="attest-statement" value={attestForm.statement} onChange={(e) => setAttestForm({ ...attestForm, statement: e.target.value })} className="rounded-none mt-1" placeholder="Leave blank to use the standard certification statement." /></div>
          </div>
          <DialogFooter><Button data-testid="attest-submit" onClick={attest} className="rounded-none"><Gavel size={14} className="mr-1" /> Sign &amp; Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScopePicker({ standards, form, setForm }) {
  return (
    <div className="space-y-3">
      <div>
        <Label>Standard</Label>
        <Select value={form.standard_code} onValueChange={(v) => setForm({ ...form, standard_code: v })}>
          <SelectTrigger data-testid="scope-standard" className="rounded-none mt-1"><SelectValue placeholder="Select a NERC standard" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {standards.map((s) => <SelectItem key={s.standard_id || s.id} value={s.standard_id}>{s.standard_id} — {(s.title || "").slice(0, 40)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Requirement (e.g. R1, R2.1)</Label>
        <Input data-testid="scope-reqnum" value={form.requirement_number} onChange={(e) => setForm({ ...form, requirement_number: e.target.value })} className="rounded-none mt-1" placeholder="R1" />
      </div>
      {"applicability_rationale" in form && (
        <div><Label>Applicability rationale</Label>
          <Textarea data-testid="scope-rationale" value={form.applicability_rationale} onChange={(e) => setForm({ ...form, applicability_rationale: e.target.value })} className="rounded-none mt-1" /></div>
      )}
    </div>
  );
}

function ErtStaging({ eid }) {
  const [data, setData] = useState({ items: [], cip_scope_count: 0 });
  const load = useCallback(() => api.get(`/engagements/${eid}/ert-readiness`).then((r) => setData(r.data)).catch(() => {}), [eid]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (key, done) => { await api.post(`/engagements/${eid}/ert-readiness`, { key, done }); load(); };
  return (
    <Section title="CIP Evidence Staging (Pre-Audit)" icon={ShieldStar}
      action={<Link to="/ert" data-testid="open-ert" className="text-xs underline text-blue-700">Open CIP ERT →</Link>}>
      <p className="text-xs text-muted-foreground mb-3">Stage evidence before the CEA issues Round-1 RFIs. {data.cip_scope_count} CIP requirement(s) in scope.</p>
      <div className="space-y-2">
        {data.items.map((it) => (
          <label key={it.key} data-testid={`ert-${it.key}`} className="flex items-center gap-2 text-sm border border-border px-3 py-2 cursor-pointer hover:bg-secondary">
            <Checkbox checked={it.done} onCheckedChange={(v) => toggle(it.key, !!v)} className="rounded-none" />
            <span className={it.done ? "line-through text-muted-foreground" : ""}>{it.label}</span>
          </label>
        ))}
      </div>
    </Section>
  );
}

function TasksPane({ eid, eng }) {
  const [assignee, setAssignee] = useState((eng.contacts || [])[0] || "");
  const [busy, setBusy] = useState(false);
  const gen = async (onlyExpanded) => {
    setBusy(true);
    try { const r = await api.post(`/engagements/${eid}/generate-tasks`, { assignee, only_expanded: onlyExpanded }); toast.success(`${r.data.created} ClaroFlow task(s) created`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Section title="ClaroFlow SME Tasks" icon={Kanban}
      action={<Link to="/claroflow" data-testid="open-claroflow" className="text-xs underline text-blue-700">Open ClaroFlow →</Link>}>
      <p className="text-xs text-muted-foreground mb-3">Generate evidence-preparation tickets for each scoped requirement, assigned to a responsible SME.</p>
      <Label>Assign to (email)</Label>
      <Input data-testid="task-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} className="rounded-none mt-1 mb-3" placeholder="sme@entity.coop" />
      <div className="flex gap-2">
        <Button data-testid="gen-tasks-btn" onClick={() => gen(false)} disabled={busy} className="rounded-none">Generate tasks for all scope</Button>
        <Button data-testid="gen-tasks-expanded-btn" onClick={() => gen(true)} disabled={busy} variant="outline" className="rounded-none">Expanded only</Button>
      </div>
    </Section>
  );
}

function ReportsPane({ eid, eng, reload }) {
  const [kind, setKind] = useState("draft");
  const [file, setFile] = useState(null);
  const [red, setRed] = useState({ excerpt: "", classification: "CEII", justification: "", page_ref: "" });
  const upload = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    try { await api.post(`/engagements/${eid}/reports`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Report uploaded"); setFile(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addRed = async () => {
    if (!red.excerpt) { toast.error("Excerpt required"); return; }
    try { await api.post(`/engagements/${eid}/redactions`, red); setRed({ excerpt: "", classification: "CEII", justification: "", page_ref: "" }); toast.success("Redaction logged"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <Section title="Audit Reports" icon={FileArrowUp}>
        {eng.engagement_type === "spot_check" && (
          <div className="text-[11px] font-semibold text-amber-800 mb-2">All Spot Check reports are permanently Non-Public / Not for Public Disclosure (Sec 4.3.1).</div>
        )}
        <div className="flex items-end gap-2 mb-3 flex-wrap">
          <div><Label>Type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="report-kind" className="rounded-none mt-1 w-32"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="final">Final</SelectItem></SelectContent>
            </Select>
          </div>
          <Input data-testid="report-file" type="file" onChange={(e) => setFile(e.target.files?.[0])} className="rounded-none" />
          <Button data-testid="report-upload" onClick={upload} className="rounded-none">Upload</Button>
        </div>
        <div className="space-y-1">
          {(eng.reports || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between text-xs border border-border px-2 py-1.5">
              <span>{r.kind === "draft" ? "📝" : "✅"} {r.filename} <span className="text-muted-foreground">· {(r.uploaded_at || "").slice(0, 10)}</span>
                {eng.engagement_type === "spot_check" && <span className="ml-1 text-[9px] font-bold uppercase text-amber-700 border border-amber-300 px-1">Non-Public</span>}
              </span>
              <button data-testid={`report-dl-${r.id}`} onClick={() => downloadBlob(`/engagements/${eid}/reports/${r.id}/download`, r.filename)} className="underline text-blue-700">download</button>
            </div>
          ))}
          {(eng.reports || []).length === 0 && <div className="text-xs text-muted-foreground">No reports uploaded.</div>}
        </div>
      </Section>
      <Section title="CEII / Confidential Redactions" icon={WarningOctagon}
        action={<Button data-testid="matrix-dl" size="sm" variant="outline" className="rounded-none h-7" onClick={() => downloadBlob(`/engagements/${eid}/redaction-matrix.docx`, "Redaction_Matrix.docx")}><DownloadSimple size={13} className="mr-1" /> Matrix</Button>}>
        <p className="text-[11px] text-muted-foreground mb-2">Tag Critical Energy/Electric Infrastructure Information and Confidential Business Information (App 4C Sec 4.1.5). Reports are Non-Public.</p>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <Input data-testid="red-excerpt" value={red.excerpt} onChange={(e) => setRed({ ...red, excerpt: e.target.value })} className="rounded-none col-span-2" placeholder="Excerpt / description to redact" />
          <Select value={red.classification} onValueChange={(v) => setRed({ ...red, classification: v })}>
            <SelectTrigger data-testid="red-class" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="CEII">CEII</SelectItem><SelectItem value="confidential_business">Confidential Business</SelectItem></SelectContent>
          </Select>
          <Input data-testid="red-page" value={red.page_ref} onChange={(e) => setRed({ ...red, page_ref: e.target.value })} className="rounded-none" placeholder="Page/ref" />
          <Input data-testid="red-just" value={red.justification} onChange={(e) => setRed({ ...red, justification: e.target.value })} className="rounded-none col-span-2" placeholder="Justification" />
        </div>
        <Button data-testid="red-add" onClick={addRed} size="sm" className="rounded-none">Log redaction</Button>
        <div className="mt-3 space-y-1">
          {(eng.redactions || []).map((r) => (
            <div key={r.id} className="text-[11px] border border-border px-2 py-1 flex items-center justify-between">
              <span><Badge variant="outline" className="rounded-none mr-1 text-[9px]">{r.classification}</Badge>{r.excerpt} {r.page_ref ? `(${r.page_ref})` : ""}</span>
              <button onClick={async () => { await api.delete(`/engagements/${eid}/redactions/${r.id}`); reload(); }} className="text-muted-foreground hover:text-red-600"><Trash size={11} /></button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function WalkdownPane({ eid, eng, reload }) {
  const [form, setForm] = useState({ facility_name: "", facility_type: "substation", walkdown_lead_sme: "", walkdown_date: "" });
  const [eq, setEq] = useState({});
  const addWd = async () => {
    if (!form.facility_name) { toast.error("Facility name required"); return; }
    try { await api.post(`/engagements/${eid}/walkdowns`, form); setForm({ facility_name: "", facility_type: "substation", walkdown_lead_sme: "", walkdown_date: "" }); toast.success("Walkdown facility added"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addEq = async (wid) => {
    const row = eq[wid] || {};
    if (!row.equipment_id && !row.type) { toast.error("Equipment id or type required"); return; }
    try { await api.post(`/engagements/${eid}/walkdowns/${wid}/equipment`, { ...row, most_limiting_element: !!row.most_limiting_element, verified_in_field: !!row.verified_in_field }); setEq({ ...eq, [wid]: {} }); toast.success(row.discrepancies_noted ? "Logged — discrepancy flagged, engineering task created" : "Equipment logged"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setWdStatus = async (wid, walkdown_status) => { await api.put(`/engagements/${eid}/walkdowns/${wid}`, { walkdown_status }); reload(); };
  return (
    <Section title="FAC-008 Substation & Line Walkdown" icon={MapTrifold}>
      <p className="text-[11px] text-muted-foreground mb-3">Verify the Most Limiting Element against the Facility Ratings Methodology. Field photos attach to each equipment row; a nameplate-vs-model conflict auto-flags a discrepancy and opens an engineering validation task.</p>
      <div className="grid grid-cols-2 gap-2 mb-3 border border-border p-3 bg-secondary/30">
        <Input data-testid="wd-name" value={form.facility_name} onChange={(e) => setForm({ ...form, facility_name: e.target.value })} className="rounded-none" placeholder="Facility (e.g. West Substation 345kV)" />
        <Select value={form.facility_type} onValueChange={(v) => setForm({ ...form, facility_type: v })}>
          <SelectTrigger data-testid="wd-type" className="rounded-none"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="substation">Substation</SelectItem>
            <SelectItem value="switchyard">Switchyard</SelectItem>
            <SelectItem value="generating_facility">Generating Facility</SelectItem>
            <SelectItem value="control_center">Control Center</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="wd-sme" value={form.walkdown_lead_sme} onChange={(e) => setForm({ ...form, walkdown_lead_sme: e.target.value })} className="rounded-none" placeholder="Lead SME email" />
        <Input data-testid="wd-date" type="date" value={form.walkdown_date} onChange={(e) => setForm({ ...form, walkdown_date: e.target.value })} className="rounded-none" />
        <Button data-testid="wd-add" onClick={addWd} className="rounded-none col-span-2 h-8">Add walkdown facility</Button>
      </div>
      <div className="space-y-3">
        {(eng.walkdowns || []).map((w) => {
          const row = eq[w.id] || {};
          return (
            <div key={w.id} data-testid={`walkdown-${w.id}`} className="border border-border">
              <div className="flex items-center justify-between px-3 py-2 bg-secondary/40">
                <span className="font-semibold text-sm">{w.facility_name} <span className="text-xs text-muted-foreground">· {w.facility_type}</span></span>
                <Select value={w.walkdown_status} onValueChange={(v) => setWdStatus(w.id, v)}>
                  <SelectTrigger data-testid={`wd-status-${w.id}`} className={`rounded-none h-6 text-[10px] w-40 ${w.walkdown_status === "findings_flagged" ? "bg-red-100 text-red-700 border-red-300" : w.walkdown_status === "completed" ? "bg-emerald-100 text-emerald-800" : ""}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="findings_flagged">Findings flagged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-3">
                <table className="w-full text-[11px] mb-2">
                  <thead><tr className="text-left text-muted-foreground"><th className="py-1">Equipment</th><th>Nameplate</th><th>Conductor</th><th>MLE</th><th>Field ✓</th><th>Discrepancy</th><th>Photos</th></tr></thead>
                  <tbody>
                    {(w.equipment || []).map((x) => (
                      <tr key={x.id} className={`border-t border-border ${x.discrepancies_noted ? "bg-red-50" : ""}`}>
                        <td className="py-1 font-mono">{x.equipment_id} <span className="text-muted-foreground">{x.type}</span></td>
                        <td>{x.nameplate_mva_amps}</td><td>{x.conductor_rating}</td>
                        <td>{x.most_limiting_element ? "★" : ""}</td><td>{x.verified_in_field ? "✓" : ""}</td>
                        <td className="text-red-700">{x.discrepancies_noted}</td>
                        <td><PhotoCell eid={eid} wid={w.id} eqid={x.id} photos={x.photos || []} reload={reload} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="grid grid-cols-6 gap-1 items-center">
                  <Input data-testid={`eq-id-${w.id}`} value={row.equipment_id || ""} onChange={(e) => setEq({ ...eq, [w.id]: { ...row, equipment_id: e.target.value } })} className="rounded-none h-7 text-xs" placeholder="ID" />
                  <Input value={row.type || ""} onChange={(e) => setEq({ ...eq, [w.id]: { ...row, type: e.target.value } })} className="rounded-none h-7 text-xs" placeholder="Type" />
                  <Input value={row.nameplate_mva_amps || ""} onChange={(e) => setEq({ ...eq, [w.id]: { ...row, nameplate_mva_amps: e.target.value } })} className="rounded-none h-7 text-xs" placeholder="Nameplate" />
                  <Input value={row.conductor_rating || ""} onChange={(e) => setEq({ ...eq, [w.id]: { ...row, conductor_rating: e.target.value } })} className="rounded-none h-7 text-xs" placeholder="Conductor" />
                  <Input data-testid={`eq-disc-${w.id}`} value={row.discrepancies_noted || ""} onChange={(e) => setEq({ ...eq, [w.id]: { ...row, discrepancies_noted: e.target.value } })} className="rounded-none h-7 text-xs" placeholder="Discrepancy" />
                  <Button data-testid={`eq-add-${w.id}`} onClick={() => addEq(w.id)} size="sm" className="rounded-none h-7 text-xs">Add</Button>
                </div>
                <div className="flex gap-3 mt-1 text-[10px]">
                  <label className="flex items-center gap-1"><Checkbox checked={!!row.most_limiting_element} onCheckedChange={(v) => setEq({ ...eq, [w.id]: { ...row, most_limiting_element: !!v } })} className="rounded-none h-3 w-3" /> Most Limiting Element</label>
                  <label className="flex items-center gap-1"><Checkbox checked={!!row.verified_in_field} onCheckedChange={(v) => setEq({ ...eq, [w.id]: { ...row, verified_in_field: !!v } })} className="rounded-none h-3 w-3" /> Verified in field</label>
                </div>
              </div>
            </div>
          );
        })}
        {(eng.walkdowns || []).length === 0 && <div className="text-xs text-muted-foreground">No walkdown facilities yet.</div>}
      </div>
    </Section>
  );
}

function PhotoCell({ eid, wid, eqid, photos, reload }) {
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/engagements/${eid}/walkdowns/${wid}/equipment/${eqid}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo attached"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <label data-testid={`eq-photo-${eqid}`} className="underline text-blue-700 cursor-pointer">
      {photos.length ? `${photos.length} 📷` : "+ photo"}
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={up} />
    </label>
  );
}

function TeamPane({ eid, eng, reload }) {
  const [m, setM] = useState({ name: "", organization: "", employment_history: "", appointed_date: "" });
  const add = async () => {
    if (!m.name) { toast.error("Name required"); return; }
    try { await api.post(`/engagements/${eid}/team`, m); setM({ name: "", organization: "", employment_history: "", appointed_date: "" }); toast.success("Team member added"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const objection = async (mid, lodged) => { await api.post(`/engagements/${eid}/team/${mid}/objection`, { lodged }); reload(); };
  const del = async (mid) => { await api.delete(`/engagements/${eid}/team/${mid}`); reload(); };
  return (
    <Section title="CEA Team & Conflict Checker" icon={Users}>
      <div className="space-y-2 mb-3">
        {(eng.team || []).map((t) => (
          <div key={t.id} data-testid={`team-${t.id}`} className="border border-border p-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="font-semibold">{t.name}</span>
              <button onClick={() => del(t.id)} className="text-muted-foreground hover:text-red-600"><Trash size={11} /></button>
            </div>
            <div className="text-muted-foreground">{t.organization}</div>
            <div className="text-[10px] mt-0.5">Objection by: <span className="font-mono">{t.objection_deadline || "—"}</span></div>
            <label className="flex items-center gap-1 mt-1">
              <Checkbox data-testid={`team-obj-${t.id}`} checked={t.objection_lodged} onCheckedChange={(v) => objection(t.id, !!v)} className="rounded-none h-3 w-3" />
              <span className="text-[10px]">Objection lodged</span>
            </label>
          </div>
        ))}
        {(eng.team || []).length === 0 && <div className="text-xs text-muted-foreground">No team members recorded.</div>}
      </div>
      <div className="grid grid-cols-2 gap-1">
        <Input data-testid="team-name" value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} className="rounded-none h-7 text-xs" placeholder="Name" />
        <Input value={m.organization} onChange={(e) => setM({ ...m, organization: e.target.value })} className="rounded-none h-7 text-xs" placeholder="Organization" />
        <Input type="date" title="Appointed date" value={m.appointed_date} onChange={(e) => setM({ ...m, appointed_date: e.target.value })} className="rounded-none h-7 text-xs col-span-2" />
        <Button data-testid="team-add" onClick={add} size="sm" className="rounded-none h-7 text-xs col-span-2">Add team member</Button>
      </div>
    </Section>
  );
}
